import { useMutation } from "@apollo/client";
import Cancel from "@mui/icons-material/Cancel";
import Person from "@mui/icons-material/Person";
import { Box, Button, FormControl, Typography } from "@mui/material";
import { gql } from "generated-graphql";
import { Permission, Role, Tenant as TenantType } from "generated-graphql/graphql";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCurrentUser } from "../hooks/useCurrentUser";
import { useAlerts } from "./Alerts/AlertProvider";
import { AssociateFacilitiesDialog } from "./AssociateFacilities/AssociateFacilitiesDialog";
import { Dialog } from "./Dialog";
import { PermissionCheckboxGrid } from "./PermissionCheckboxGrid";
import { RolePicker } from "./RolePicker";
import { TenantPicker, Tenant as TenantPickerType } from "./TenantPicker";

const SET_EMULATED_ROLE = gql(`
  mutation SetEmulatedRole($tenantId: String!, $permissions: [Permission!]!, $facilityIds: [ID!], $roleId: ID) {
    setEmulatedRole(tenantId: $tenantId, permissions: $permissions, facilityIds: $facilityIds, roleId: $roleId)
  }
`);

const RESET_EMULATED_ROLE = gql(`
  mutation ResetEmulatedRole {
    resetEmulatedRole
  }
`);

function FacilityAccessNotice({
  selectedPermissions,
  selectedFacilityCount,
  onSelectFacilities,
  onClearFacilities,
}: {
  selectedPermissions: Permission[];
  selectedFacilityCount: number;
  onSelectFacilities: () => void;
  onClearFacilities: () => void;
}) {
  const canFulfillTasks = selectedPermissions.includes(Permission.FulfillDataCollectionTask);
  const canAccessAllFacilities =
    selectedPermissions.includes(Permission.ReadAllFacility) ||
    selectedPermissions.includes(Permission.WriteAllFacility);
  if (!canFulfillTasks) {
    return null;
  }
  if (canAccessAllFacilities) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          ℹ️ Facility selection is not needed - this role has access to all facilities in the
          tenant.
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Facility Access
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select facilities to grant access to. Leave empty to test users with no facility access.
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button variant="outlined" onClick={onSelectFacilities}>
          Select Facilities ({selectedFacilityCount})
        </Button>
        {selectedFacilityCount > 0 && (
          <Button size="small" color="error" onClick={onClearFacilities}>
            Clear All
          </Button>
        )}
      </Box>
    </Box>
  );
}

type EmulatePermissionsDialogProps = {
  open: boolean;
  tenant?: TenantType | TenantPickerType;
  onClose: () => void;
};

export function EmulatePermissionsDialog({
  open,
  tenant: initialTenant,
  onClose,
}: EmulatePermissionsDialogProps) {
  const { user, refetchUser } = useCurrentUser();
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([]);
  const alerts = useAlerts();
  const navigate = useNavigate();
  const [setEmulatedRole, { loading: emulateLoading }] = useMutation(SET_EMULATED_ROLE);
  const [resetEmulatedRole, { loading: resetLoading }] = useMutation(RESET_EMULATED_ROLE);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [tenant, setTenant] = useState<TenantType | TenantPickerType | undefined>(initialTenant);
  const [redirectAfterUserUpdate, setRedirectAfterUserUpdate] = useState<string | undefined>(
    undefined
  );
  const [tenantPickerOpen, setTenantPickerOpen] = useState(false);
  const [facilitiesDialogOpen, setFacilitiesDialogOpen] = useState(false);

  useEffect(() => {
    if (redirectAfterUserUpdate) {
      navigate(redirectAfterUserUpdate);
      setRedirectAfterUserUpdate(undefined);
    }
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
  }, [user]);

  useEffect(() => {
    if (selectedRole) {
      const rolePermissions = selectedRole.permissions as Permission[];
      setSelectedPermissions(rolePermissions);

      // Clear facility selections if role has access to all facilities
      if (
        rolePermissions.includes(Permission.ReadAllFacility) ||
        rolePermissions.includes(Permission.WriteAllFacility)
      ) {
        setSelectedFacilityIds([]);
      }
    }
  }, [selectedRole, user?.isEmulating]);

  useEffect(() => {
    if (initialTenant) {
      setTenant(initialTenant);
    }
  }, [initialTenant]);

  const handleEmulate = async () => {
    try {
      if (!tenant) {
        alerts.error("Please select a tenant");
        return;
      }
      await setEmulatedRole({
        variables: {
          tenantId: tenant?.id ?? "",
          permissions: selectedPermissions,
          facilityIds: selectedFacilityIds,
          roleId: selectedRole?.id,
        },
      });
      alerts.success("Successfully emulated user");
      setRedirectAfterUserUpdate(`/`);
      refetchUser();
      onClose();
    } catch (error) {
      alerts.error("Failed to emulate user");
    }
  };

  const handleResetEmulation = async () => {
    try {
      await resetEmulatedRole();
      alerts.success("Successfully reset emulation");
      if (tenant) {
        setRedirectAfterUserUpdate(`/o/${tenant.id}`);
      }
      refetchUser();
      onClose();
    } catch (error) {
      alerts.error("Failed to reset emulation");
    }
  };

  const handlePermissionsChange = (next: Permission[]) => {
    const gainedAllFacilityAccess =
      (!selectedPermissions.includes(Permission.ReadAllFacility) &&
        next.includes(Permission.ReadAllFacility)) ||
      (!selectedPermissions.includes(Permission.WriteAllFacility) &&
        next.includes(Permission.WriteAllFacility));
    if (gainedAllFacilityAccess) {
      setSelectedFacilityIds([]);
    }
    setSelectedPermissions(next);
  };

  const handleTenantSelected = (selectedTenant: TenantPickerType) => {
    setTenant(selectedTenant);
    setTenantPickerOpen(false);
  };

  const handleOnClose = () => {
    setTenantPickerOpen(false);
    setTenant(initialTenant);
    setSelectedRole(null);
    setSelectedPermissions([]);
    setSelectedFacilityIds([]);
    setFacilitiesDialogOpen(false);
    onClose();
  };

  if (user?.isEmulating) {
    return (
      <Dialog
        open={open}
        onClose={handleOnClose}
        maxWidth="lg"
        title="Emulate Permissions"
        primaryActionButtonProps={{
          loading: emulateLoading || resetLoading,
          onClick: handleResetEmulation,
          startIcon: <Cancel />,
          disabled: !tenant,
          saveText: "Reset",
        }}
      >
        <Typography>
          You are currently emulating a role in {tenant?.name}. Please reset to emulate a different
          role or to go back to being a staff user.
        </Typography>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      maxWidth="lg"
      title="Emulate Permissions"
      primaryActionButtonProps={{
        loading: emulateLoading || resetLoading,
        onClick: handleEmulate,
        startIcon: <Person />,
        disabled: !tenant || selectedPermissions.length === 0,
        saveText: "Emulate",
      }}
      description="Select a tenant and role to view that tenant as a specific user role, or
      select a combination of permissions to see what a user with those
      permissions would see."
    >
      {!!tenant && (
        <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
          <Typography>Current Tenant: {tenant.name}</Typography>
          <Button onClick={() => setTenantPickerOpen(!tenantPickerOpen)}>Change</Button>
        </Box>
      )}
      {(tenantPickerOpen || !tenant) && <TenantPicker onTenantSelected={handleTenantSelected} />}
      {!!tenant && (
        <>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <RolePicker
              tenantId={tenant?.id ?? ""}
              value={selectedRole?.id ?? ""}
              onChange={setSelectedRole}
            />
          </FormControl>

          <FacilityAccessNotice
            selectedPermissions={selectedPermissions}
            selectedFacilityCount={selectedFacilityIds.length}
            onSelectFacilities={() => setFacilitiesDialogOpen(true)}
            onClearFacilities={() => setSelectedFacilityIds([])}
          />

          <Box sx={{ p: 2 }}>
            <PermissionCheckboxGrid
              selectedPermissions={selectedPermissions}
              onChange={handlePermissionsChange}
            />
          </Box>
        </>
      )}

      {/* Associate Facilities Dialog */}
      <AssociateFacilitiesDialog
        open={facilitiesDialogOpen}
        onClose={() => setFacilitiesDialogOpen(false)}
        existingFacilityIds={selectedFacilityIds}
        onSave={(facilityIds) => {
          setSelectedFacilityIds(facilityIds);
          setFacilitiesDialogOpen(false);
        }}
        subtitle="Select facilities to grant access to for this emulated role."
        allFacilities={false}
        showTenantColumn={false}
      />
    </Dialog>
  );
}
